import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Divider,
  Stack,
  Autocomplete,
  Tooltip,
  InputAdornment,
  Grid,
  CircularProgress,
  Checkbox,
  Snackbar,
  Alert,
} from "@common-ui/ui";

import { useParams, useHistory } from "react-router-dom";
import {
  Business,
  AlternateEmail,
  Tv,
  Info,
  Email,
  Phone,
  LinkedIn,
  Facebook,
  Twitter,
  YouTube,
  Link,
} from "@mui/icons-material";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddIcon from "@mui/icons-material/Add";

import {
  countries,
  updateOrg,
  getOrg,
  getOrgProfileImage,
  updateOrgProfileImage,
  putOrgProfileImage,
  getOrgUsers,
  getUserOrgs,
} from "../../services";
import { FormControlLabel } from "@mui/material";
import AlertDialog from "./AlertDialog";
import Address from "./Address";
import { Places } from "./places";
import UsersTableView from "./UsersTableView";
import UsersInvitationDialog from "./UsersInvitationDialog";
import DomainInputTags from "./DomainInputTags";
import { useDispatch, useSelector } from "react-redux";
import { organizationSlice } from "../../reducers/organizationReducer";
import MandatoryIcon from "../../components/MandatoryIcon";

export const EditOrgForm = () => {
  let router = useHistory();
  let params = useParams();
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const currentOrganization = useSelector(
    (state) => state.organization.currentOrganization
  );
  let appRole = currentOrganization?.app_role;
  let doesUserHavePermission = appRole === "ADMIN";

  const tagRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = React.useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [orgImageFileType, setOrgImageFileType] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [org, setOrg] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);

  const [inviteDialog, setSendInviteDialog] = useState({ isOpen: false });
  const [toast, setToast] = useState([]);
  const [showDomainRestrictions, setShowDomainRestrictions] = useState(false);
  const [domainValues, setDomainValues] = useState("");
  const [tagValues, setTagValues] = React.useState({
    tag: "",
    tags: [],
  });

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function init() {
    if (params.id === "undefined" || !params.id) {
      setIsLoading(false);
      return;
    }

    let payload = await getOrg(params.id);
    if (!payload) return;
    setOrg(payload);

    let users = await getOrgUsers(params.id);
    setOrgUsers(users);

    let currentOrg = null;
    const orgs = await getUserOrgs();
    if (orgs?.length === 1) {
      currentOrg = orgs[0];
      dispatch(organizationSlice.actions.setOrganizations(orgs));
      dispatch(organizationSlice.actions.setCurrentOrganization(currentOrg));
    }

    let {
      // id,
      // parent,
      name,
      screen_name,
      handle,
      // description,
      // logo,
      // type,
      support_phone,
      support_email,
      website,
      linkedin,
      twitter,
      facebook,
      youtube,
      street_address,
      shipping_address,
      headquarters,
      logo,
      domains,
    } = payload;
    setAddresses([
      {
        ...street_address,
        type: "Mailing",
      },
      {
        ...shipping_address,
        type: "Shipping",
      },
    ]);
    let address_line_1 = street_address?.address_line_1;
    let address_line_2 = street_address?.address_line_2;
    let city = street_address?.city;
    let state = street_address?.state_or_province;
    let postal_code = street_address?.postal_code;
    let country = street_address?.country_or_region;

    let shipping_address_line_1 = shipping_address?.address_line_1;
    let shipping_address_line_2 = shipping_address?.address_line_2;
    let shipping_city = shipping_address?.city;
    let shipping_state = shipping_address?.state_or_province;
    let shipping_postal_code = shipping_address?.postal_code;
    let shipping_country = shipping_address?.country_or_region;

    formik.setFieldValue("orgName", name);
    formik.setFieldValue("orgHandle", handle);
    formik.setFieldValue("orgDisplayName", screen_name?.trim());
    let domainData = domains.filter((item) => item);
    formik.setFieldValue("domains", domainData);
    if (domainData.length > 0) {
      setTagValues({ tags: domainData });
      setShowDomainRestrictions(true);
    }
    // // Contact Info
    support_email && formik.setFieldValue("email", support_email?.trim());
    support_phone && formik.setFieldValue("phone", support_phone?.trim());
    website && formik.setFieldValue("website", website?.trim());
    linkedin && formik.setFieldValue("linkedin", linkedin?.trim());
    facebook && formik.setFieldValue("facebook", facebook?.trim());
    twitter && formik.setFieldValue("twitter", twitter?.trim());
    youtube && formik.setFieldValue("youtube", youtube?.trim());
    address_line_1 &&
      formik.setFieldValue("address_line_1", address_line_1?.trim());
    address_line_2 &&
      formik.setFieldValue("address_line_2", address_line_2?.trim());
    city && formik.setFieldValue("city", city?.trim());
    state && formik.setFieldValue("state", state?.trim());
    postal_code && formik.setFieldValue("postal_code", postal_code?.trim());
    // Filter country
    let countryData = Array.from(countries).filter(
      (option) => option.label === country
    );
    country && formik.setFieldValue("country", countryData[0] || null);
    shipping_address_line_1 &&
      formik.setFieldValue(
        "shipping_address_line_1",
        shipping_address_line_1?.trim()
      );
    shipping_address_line_2 &&
      formik.setFieldValue("shipping_address_line_2", shipping_address_line_2);
    shipping_city &&
      formik.setFieldValue("shipping_city", shipping_city?.trim());
    shipping_state &&
      formik.setFieldValue("shipping_state", shipping_state?.trim());
    shipping_postal_code &&
      formik.setFieldValue(
        "shipping_postal_code",
        shipping_postal_code?.trim()
      );
    let shippingCountryData = Array.from(countries).filter(
      (option) => option.label === shipping_country
    );
    shipping_country &&
      formik.setFieldValue("shipping_country", shippingCountryData[0] || null);
    headquarters && formik.setFieldValue("headquarters", headquarters?.trim());

    if (
      address_line_1 === shipping_address_line_1 &&
      address_line_2 === shipping_address_line_2 &&
      city === shipping_city &&
      state === shipping_state &&
      country === shipping_country &&
      postal_code === shipping_postal_code
    ) {
      formik.setFieldValue("shipping_same_as_mailing", true);
    }
    if (logo && logo.length) {
      const imageType = logo
        .split("?")[0]
        .split("/")
        .slice(-1)[0]
        .split(".")[1];
      setOrgImageFileType(imageType);
    }
    setIsLoading(false);
  }
  const formik = useFormik({
    initialValues: {
      orgName: "",
      orgHandle: "",
      orgDisplayName: "",
      // Contact Info
      email: "",
      phone: "",
      website: "",
      linkedin: "",
      facebook: "",
      twitter: "",
      youtube: "",
      // Mailing Address
      address_line_1: null,
      address_line_2: "",
      city: "",
      state: "",
      country: null,
      postal_code: "",
      // Shipping Address
      shipping_address_line_1: "",
      shipping_address_line_2: "",
      shipping_city: "",
      shipping_state: "",
      shipping_country: null,
      shipping_postal_code: "",
      shipping_same_as_mailing: false,
      domains: null,
    },
    validationSchema: Yup.object({
      orgName: Yup.string().trim().max(255).required("Name is required"),
      orgHandle: Yup.string().trim().max(255).required("Handle is required"),
      orgDisplayName: Yup.string().trim().max(255),

      // Contact Info
      email: Yup.string().email().trim(),
      phone: Yup.number(),
      website: Yup.string().url().trim(),
      linkedin: Yup.string().url().trim(),
      facebook: Yup.string().url().trim(),
      twitter: Yup.string().url().trim(),
      youtube: Yup.string().url().trim(),

      // Mailing Address
      address_line_1: Yup.string().trim(),
      address_line_2: Yup.string().trim(),
      city: Yup.string().trim(),
      state: Yup.string().trim(),
      country: Yup.object().nullable().required("Country / Region is required"),
      postal_code: Yup.string().trim(),

      // Shipping Address
      shipping_address_line_1: Yup.string().trim(),
      shipping_address_line_2: Yup.string().trim(),
      shipping_city: Yup.string().trim(),
      shipping_state: Yup.string().trim(),
      shipping_country: Yup.object().nullable(),
      shipping_postal_code: Yup.string().trim(),
      shipping_same_as_mailing: Yup.boolean().oneOf([true, false]),
      domain: Yup.object().nullable(),
    }),
    onSubmit: () => {
      async function submit() {
        let {
          orgName,
          orgHandle,
          orgDisplayName,
          // Contact Info
          email,
          phone,
          website,
          linkedin,
          facebook,
          twitter,
          youtube,
          // Mailing Address
          address_line_1,
          address_line_2,
          city,
          state,
          country,
          postal_code,
          // Shipping Address
          shipping_address_line_1,
          shipping_address_line_2,
          shipping_city,
          shipping_state,
          shipping_country,
          shipping_postal_code,
          shipping_same_as_mailing,
        } = formik.values;
        let attributes = {
          name: orgName,
          handle: orgHandle,
          screen_name: orgDisplayName,
          support_email: email || " ",
          support_phone: phone,

          website,
          linkedin,
          facebook,
          twitter,
          youtube,

          street_address: {
            // Mailing Address
            address_line_1: address_line_1 || " ",
            address_line_2: address_line_2 || " ",
            city: city || " ",
            state_or_province: state || " ",
            country_or_region: country?.label || " ",
            postal_code: postal_code || " ",
          },
          shipping_address: {
            // Shipping Address
            address_line_1: shipping_address_line_1 || " ",
            address_line_2: shipping_address_line_2 || " ",
            city: shipping_city || " ",
            state_or_province: shipping_state || " ",
            country_or_region: shipping_country?.label || " ",
            postal_code: shipping_postal_code || " ",
          },
          shipping_same_as_mailing,
          domains: showDomainRestrictions === false ? [] : tagValues.tags || [],
        };
        try {
          attributes = Object.keys(attributes).reduce((acc, key) => {
            acc[key] =
              typeof attributes[key] === "string" && attributes[key] === ""
                ? " "
                : attributes[key];
            return acc;
          }, {});
          await onSubmit(attributes);
          setOpen(true);
        } catch (error) {
          console.log(error);
          setToast([
            ...toast,
            {
              message: error,
              open: true,
              severity: "error",
            },
          ]);
          formik.setSubmitting(false);
        }
      }
      submit();
    },
  });

  useEffect(() => {
    if (formik.values.shipping_same_as_mailing) {
      formik.setFieldValue(
        "shipping_address_line_1",
        formik.values.address_line_1
      );
      formik.setFieldValue(
        "shipping_address_line_2",
        formik.values.address_line_2
      );
      formik.setFieldValue("shipping_city", formik.values.city);
      formik.setFieldValue("shipping_state", formik.values.state);
      formik.setFieldValue("shipping_postal_code", formik.values.postal_code);
      formik.setFieldValue("shipping_country", formik.values.country);
    } else {
      formik.setFieldValue("shipping_address_line_1", "");
      formik.setFieldValue("shipping_address_line_2", "");
      formik.setFieldValue("shipping_city", "");
      formik.setFieldValue("shipping_state", "");
      formik.setFieldValue("shipping_postal_code", "");
      formik.setFieldValue("shipping_country", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.shipping_same_as_mailing,
    formik.values.address_line_1,
    formik.values.address_line_2,
    formik.values.city,
    formik.values.country,
    formik.values.state,
    formik.values.postal_code,
  ]);

  useEffect(() => {
    async function getImage() {
      try {
        const response = await getOrgProfileImage(params.id, orgImageFileType);
        setImageUrl(response.data.url);
      } catch (error) {
        setImageUrl("");
        console.log(error);
      }
    }
    if (orgImageFileType?.length) getImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgImageFileType]);

  const onSubmit = async (attributes) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const payload = await updateOrg(attributes, params.id);
        resolve({ payload: payload });
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleClose = () => {
    router.push({
      pathname: `/orgs/tree-view/overview`,
    });
    setOpen(false);
  };

  const handleUserInviteDialogClose = async () => {
    setSendInviteDialog({ ...inviteDialog, isOpen: false });
  };
  const handleUserInviteDialogOpen = () => {
    setSendInviteDialog({ ...inviteDialog, isOpen: true });
  };

  const handleShowDomainRestriction = () => {
    setShowDomainRestrictions((oldState) => !oldState);
  };

  const handleDomainRestrictions = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setTagValues({
        tags: [...tagValues.tags, tagRef.current.value],
        tag: "",
      });
      tagRef.current.value = "";
    }
  };

  useEffect(() => {
    if (tagValues.tags) {
      setDomainValues(tagValues.tags);
    }
  }, [tagValues.tags]);

  useEffect(() => {
    if (!showDomainRestrictions) {
      setTagValues({
        tags: [],
        tag: "",
      });
    }
  }, [showDomainRestrictions]);

  const handleImageSelection = async (file) => {
    if (!/image\/[jpeg|png|svg+xml]/gm.test(file?.type)) {
      setToast([
        ...toast,
        {
          message: `Please upload only images!`,
          open: true,
          severity: "error",
        },
      ]);
    } else {
      try {
        const response = await updateOrgProfileImage(
          params.id,
          file.type.split("/")[1]
        );
        if (response.status === 200 && response.data.putUrl) {
          const res = await putOrgProfileImage(
            response.data.putUrl,
            file,
            file.type.split("/")[1]
          );
          if (res.status === 200) {
            await onSubmit({
              logo: response.data.getUrl,
            });
            setImageUrl(response.data.getUrl);
          }
        }
      } catch (error) {
        setToast([
          ...toast,
          {
            message: error,
            open: true,
            severity: "error",
          },
        ]);
      }
    }
  };

  const removeImage = async () => {
    await onSubmit({
      logo: "",
    });
    setImageUrl("");
  };

  return isLoading ? (
    <Box
      component="main"
      sx={{
        alignItems: "center",
        display: "flex",
        flexGrow: 1,
        minHeight: "100%",
        ".MuiDataGrid-virtualScroller": {
          marginTop: "40px !important",
        },
        // bgcolor: "background.paper",
      }}
    >
      <Container maxWidth="md" component="main">
        <Box sx={{ my: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    </Box>
  ) : params.id === "undefined" || !params.id ? (
    <Typography
      sx={{ mt: 2, textAlign: "center" }}
      color="textPrimary"
      variant="h4"
    >
      You do not have any organizations at the moment.
    </Typography>
  ) : (
    <>
      <Box
        component="main"
        sx={{
          alignItems: "center",
          display: "flex",
          flexGrow: 1,
          minHeight: "100%",
          backgroundColor: "#FFFFFF",
          ".MuiDataGrid-virtualScroller": {
            marginTop: "40px !important",
          },
          ".MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
          // bgcolor: "background.paper",
        }}
      >
        <Container maxWidth="md" component="main">
          <form onSubmit={formik.handleSubmit}>
            <Stack direction="column" sx={{ maxWidth: { md: 600 }, my: 3 }}>
              <Grid container spacing={2}>
                <Grid container spacing={2}>
                  <Grid item md={6} xs={12}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        margin: "15% 5% 0 0",
                      }}
                    >
                      {imageUrl && imageUrl.length > 0 ? (
                        // eslint-disable-next-line jsx-a11y/img-redundant-alt
                        <img
                          alt="profile-image"
                          src={imageUrl}
                          style={{
                            height: "100%",
                            width: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <InsertPhotoOutlinedIcon
                          fontSize="large"
                          sx={{
                            height: "4.2em",
                            width: "6.2em",
                          }}
                        />
                      )}
                      <div
                        style={{
                          display: "inline-flex",
                        }}
                      >
                        <Button
                          disabled={!doesUserHavePermission}
                          fullWidth
                          size="small"
                          onClick={() => inputRef.current.click()}
                          variant="text"
                        >
                          <EditOutlinedIcon
                            fontSize="small"
                            sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                          />
                          <input
                            multiple={false}
                            onChange={() =>
                              handleImageSelection(inputRef.current.files[0])
                            }
                            style={{ display: "none" }}
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                          />
                        </Button>

                        {imageUrl && imageUrl.length > 0 && (
                          <Button
                            fullWidth
                            size="small"
                            variant="text"
                            onClick={removeImage}
                          >
                            <CloseOutlinedIcon
                              fontSize="small"
                              sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                            />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Typography color="textPrimary" variant="h4">
                      Organization Information
                    </Typography>
                    <Grid item md={12} xs={12}>
                      <div>
                        <Typography>
                          <MandatoryIcon />
                          Company or Organization Name
                        </Typography>
                        <TextField
                          autoComplete="off"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Business />
                              </InputAdornment>
                            ),
                          }}
                          size="small"
                          error={Boolean(
                            formik.touched.orgName && formik.errors.orgName
                          )}
                          fullWidth
                          helperText={
                            formik.touched.orgName && formik.errors.orgName
                          }
                          placeholder="Type"
                          disabled
                          margin="normal"
                          sx={{ mt: 1 }}
                          name="orgName"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.orgName}
                        />
                      </div>
                    </Grid>
                    <Grid item md={12} xs={12}>
                      <div>
                        <Typography>
                          <MandatoryIcon />
                          @Org_handle{" "}
                          <span>
                            <Tooltip
                              placement="right"
                              title="For use in mentions i.e. @org_handle, you can use any
                    handle that is unique, and can choose to keep it
                    consistent to match your handle on other platforms e.g.
                    twitter."
                            >
                              <Info sx={{ fontSize: 14, color: "#777777" }} />
                            </Tooltip>
                          </span>
                        </Typography>
                        <TextField
                          size="small"
                          sx={{ mt: 1 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <AlternateEmail />
                              </InputAdornment>
                            ),
                          }}
                          autoComplete="off"
                          disabled
                          error={Boolean(
                            formik.touched.orgHandle && formik.errors.orgHandle
                          )}
                          fullWidth
                          helperText={
                            formik.touched.orgHandle && formik.errors.orgHandle
                          }
                          placeholder="Type"
                          margin="normal"
                          name="orgHandle"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          value={formik.values.orgHandle}
                        />
                      </div>
                    </Grid>
                    <Grid item md={12} xs={12}>
                      {" "}
                      <div>
                        <Typography>
                          Display Name{" "}
                          <span>
                            <Tooltip
                              placement="right"
                              title="You can use a display name different from your
                        organization name, or you can leave this blank. You can
                        update your display name in your organization profile."
                            >
                              <Info sx={{ fontSize: 14, color: "#777777" }} />
                            </Tooltip>
                          </span>
                        </Typography>
                        <TextField
                          sx={{ mt: 1 }}
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tv />
                              </InputAdornment>
                            ),
                          }}
                          autoComplete="off"
                          error={Boolean(
                            formik.touched.orgDisplayName &&
                            formik.errors.orgDisplayName
                          )}
                          fullWidth
                          helperText={
                            formik.touched.orgDisplayName &&
                            formik.errors.orgDisplayName
                          }
                          placeholder="Type"
                          margin="normal"
                          name="orgDisplayName"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          type="orgDisplayName"
                          value={formik.values.orgDisplayName}
                          variant="outlined"
                          disabled={!doesUserHavePermission}
                        />
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
                {/* Contact Info */}
                <Grid item md={12} xs={12}>
                  <Divider />
                  <Typography sx={{ mt: 2 }} color="textPrimary" variant="h4">
                    Contact Information
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  {" "}
                  <div>
                    <Typography>Email</Typography>
                    <TextField
                      sx={{ mt: 1 }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Email />
                          </InputAdornment>
                        ),
                      }}
                      autoComplete="off"
                      error={Boolean(
                        formik.touched.email && formik.errors.email
                      )}
                      fullWidth
                      helperText={formik.touched.email && formik.errors.email}
                      placeholder="Type"
                      margin="normal"
                      name="email"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="email"
                      value={formik.values.email}
                      variant="outlined"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>
                <Grid item md={6} xs={12}>
                  {" "}
                  <div>
                    <Typography>Phone</Typography>

                    <Stack direction="row">
                      <TextField
                        sx={{ mt: 1 }}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Phone />
                            </InputAdornment>
                          ),
                        }}
                        autoComplete="off"
                        error={Boolean(
                          formik.touched.phone && formik.errors.phone
                        )}
                        fullWidth
                        helperText={formik.touched.phone && formik.errors.phone}
                        placeholder="Type"
                        margin="normal"
                        name="phone"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type="number"
                        value={formik.values.phone}
                        variant="outlined"
                        disabled={!doesUserHavePermission}
                      />
                    </Stack>
                  </div>
                </Grid>
                <Grid item md={6} xs={12}>
                  {" "}
                  <div>
                    <Typography>Website</Typography>
                    <TextField
                      sx={{ mt: 1 }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Link />
                          </InputAdornment>
                        ),
                      }}
                      autoComplete="off"
                      error={Boolean(
                        formik.touched.website && formik.errors.website
                      )}
                      fullWidth
                      helperText={
                        formik.touched.website && formik.errors.website
                      }
                      placeholder="Type"
                      margin="normal"
                      name="website"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="website"
                      value={formik.values.website}
                      variant="outlined"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>
                <Grid item md={6} xs={12}>
                  {" "}
                  <div>
                    <Typography>Linkedin</Typography>
                    <TextField
                      sx={{ mt: 1 }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <LinkedIn />
                          </InputAdornment>
                        ),
                      }}
                      autoComplete="off"
                      error={Boolean(
                        formik.touched.linkedin && formik.errors.linkedin
                      )}
                      fullWidth
                      helperText={
                        formik.touched.linkedin && formik.errors.linkedin
                      }
                      placeholder="Type"
                      margin="normal"
                      name="linkedin"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="linkedin"
                      value={formik.values.linkedin}
                      variant="outlined"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>
                <Grid item md={6} xs={12}>
                  {" "}
                  <div>
                    <Typography>Facebook</Typography>
                    <TextField
                      sx={{ mt: 1 }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Facebook />
                          </InputAdornment>
                        ),
                      }}
                      autoComplete="off"
                      error={Boolean(
                        formik.touched.facebook && formik.errors.facebook
                      )}
                      fullWidth
                      helperText={
                        formik.touched.facebook && formik.errors.facebook
                      }
                      placeholder="Type"
                      margin="normal"
                      name="facebook"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="facebook"
                      value={formik.values.facebook}
                      variant="outlined"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  {" "}
                  <div>
                    <Typography>Twitter</Typography>
                    <TextField
                      sx={{ mt: 1 }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Twitter />
                          </InputAdornment>
                        ),
                      }}
                      autoComplete="off"
                      error={Boolean(
                        formik.touched.twitter && formik.errors.twitter
                      )}
                      fullWidth
                      helperText={
                        formik.touched.twitter && formik.errors.twitter
                      }
                      placeholder="Type"
                      margin="normal"
                      name="twitter"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="twitter"
                      value={formik.values.twitter}
                      variant="outlined"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  {" "}
                  <div>
                    <Typography>Youtube</Typography>
                    <TextField
                      sx={{ mt: 1 }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <YouTube />
                          </InputAdornment>
                        ),
                      }}
                      autoComplete="off"
                      error={Boolean(
                        formik.touched.youtube && formik.errors.youtube
                      )}
                      fullWidth
                      helperText={
                        formik.touched.youtube && formik.errors.youtube
                      }
                      placeholder="Type"
                      margin="normal"
                      name="youtube"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="youtube"
                      value={formik.values.youtube}
                      variant="outlined"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                {/* Mailing Address */}
                <Grid item md={12} xs={12}>
                  <Divider />
                  <Typography sx={{ mt: 2 }} color="textPrimary" variant="h4">
                    Mailing address
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>Address 1</Typography>
                    <Address
                      formik={formik}
                      field="address_line_1"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>Address 2</Typography>
                    <TextField
                      autoComplete="off"
                      size="small"
                      error={Boolean(
                        formik.touched.address_line_2 &&
                        formik.errors.address_line_2
                      )}
                      fullWidth
                      helperText={
                        formik.touched.address_line_2 &&
                        formik.errors.address_line_2
                      }
                      placeholder="Type"
                      // label="Company or Organization Name"
                      margin="normal"
                      sx={{ mt: 1 }}
                      name="address_line_2"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.address_line_2}
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>
                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>City</Typography>
                    <Address
                      formik={formik}
                      field="city"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>State/Province</Typography>
                    <Address
                      formik={formik}
                      field="state"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>
                      <MandatoryIcon />
                      Country / Region
                    </Typography>
                    <Autocomplete
                      disabled={!doesUserHavePermission}
                      id="country-select"
                      sx={{ mt: 1 }}
                      disablePortal
                      ListboxProps={{ style: { maxHeight: "200px" } }}
                      options={countries}
                      autoHighlight
                      size="small"
                      getOptionLabel={(option) => option.label}
                      margin="normal"
                      name="country"
                      limitTags={4}
                      onBlur={formik.handleBlur}
                      onChange={(value, newValue) => {
                        formik.setFieldValue("country", newValue);
                      }}
                      fullWidth
                      value={formik.values.country}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                          {...props}
                        >
                          <img
                            loading="lazy"
                            width="20"
                            src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                            alt=""
                          />
                          {option.label} ({option.code}) +{option.phone}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select"
                          helperText={
                            formik.touched.country && formik.errors.country
                          }
                          error={Boolean(
                            formik.touched.country && formik.errors.country
                          )}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: "off", // disable autocomplete and autofill
                          }}
                        />
                      )}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>Zip/Postal Code</Typography>
                    <Address
                      formik={formik}
                      field="postal_code"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                {/* Shipping Address */}
                <Grid item md={12} xs={12}>
                  <Divider />
                  <Typography sx={{ mt: 2 }} color="textPrimary" variant="h4">
                    Shipping address{" "}
                    <FormControlLabel
                      control={<Checkbox />}
                      label="Same as Mailing Address"
                      name="shipping_same_as_mailing"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.shipping_same_as_mailing}
                      checked={formik.values.shipping_same_as_mailing}
                      disabled={!doesUserHavePermission}
                    />
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>Address 1</Typography>
                    <Address
                      formik={formik}
                      field="shipping_address_line_1"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>Address 2</Typography>
                    <TextField
                      autoComplete="off"
                      size="small"
                      error={Boolean(
                        formik.touched.shipping_address_line_2 &&
                        formik.errors.shipping_address_line_2
                      )}
                      fullWidth
                      helperText={
                        formik.touched.shipping_address_line_2 &&
                        formik.errors.shipping_address_line_2
                      }
                      placeholder="Type"
                      // label="Company or Organization Name"
                      margin="normal"
                      sx={{ mt: 1 }}
                      name="shipping_address_line_2"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.shipping_address_line_2}
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>
                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>City</Typography>
                    <Address
                      formik={formik}
                      field="shipping_city"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>State/Province</Typography>
                    <Address
                      formik={formik}
                      field="shipping_state"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>Country / Region</Typography>
                    <Autocomplete
                      disabled={!doesUserHavePermission}
                      id="country-select"
                      sx={{ mt: 1 }}
                      disablePortal
                      ListboxProps={{ style: { maxHeight: "200px" } }}
                      options={countries}
                      autoHighlight
                      size="small"
                      getOptionLabel={(option) => option.label}
                      margin="normal"
                      name="shipping_country"
                      limitTags={4}
                      onBlur={formik.handleBlur}
                      onChange={(value, newValue) => {
                        formik.setFieldValue("shipping_country", newValue);
                      }}
                      fullWidth
                      value={formik.values.shipping_country}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                          {...props}
                        >
                          <img
                            loading="lazy"
                            width="20"
                            src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                            alt=""
                          />
                          {option.label} ({option.code}) +{option.phone}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select"
                          helperText={
                            formik.touched.shipping_country &&
                            formik.errors.shipping_country
                          }
                          error={Boolean(
                            formik.touched.shipping_country &&
                            formik.errors.shipping_country
                          )}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: "off", // disable autocomplete and autofill
                          }}
                        />
                      )}
                    />
                  </div>
                </Grid>

                <Grid item md={6} xs={12}>
                  <div>
                    <Typography>Zip/Postal Code</Typography>
                    <Address
                      formik={formik}
                      field="shipping_postal_code"
                      disabled={!doesUserHavePermission}
                    />
                  </div>
                </Grid>
              </Grid>
            </Stack>

            {/* Users of Org */}
            <Grid item md={12} xs={12} mb={5} mt={2}>
              <Divider />
              <Stack direction="row" sx={{ maxWidth: { md: 800 } }}>
                <Grid item md={6} xs={12}>
                  <Typography sx={{ my: 2 }} color="textPrimary" variant="h3">
                    Users
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  {doesUserHavePermission ? (
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      sx={{ my: 3, ml: 2 }}
                      onClick={() => handleUserInviteDialogOpen()}
                    >
                      <AddIcon />
                      Add New
                    </Button>
                  ) : (
                    ""
                  )}
                </Grid>
              </Stack>

              <Stack direction="row">
                <UsersTableView users={orgUsers} isShowTableHeader={false} />
              </Stack>

              <Stack direction="row">
                <Checkbox
                  name="isDomainRestricationRequired"
                  onChange={handleShowDomainRestriction}
                  checked={showDomainRestrictions}
                  disabled={!doesUserHavePermission}
                />
                <Typography sx={{ my: 2 }} color="textPrimary">
                  Restrict registrations to specific domains
                </Typography>
              </Stack>

              <Stack direction="row" style={{ mb: 5 }}>
                {showDomainRestrictions ? (
                  <div style={{ width: "100%" }}>
                    <DomainInputTags
                      onDomainChange={handleDomainRestrictions}
                      tagValues={tagValues}
                      setTagValues={setTagValues}
                      forwardedRef={tagRef}
                      doesUserHavePermission={!doesUserHavePermission}
                    />
                  </div>
                ) : (
                  ""
                )}
              </Stack>
            </Grid>
            {toast.map((item, id) => {
              return (
                <Snackbar
                  key={id}
                  open={item.open}
                  autoHideDuration={6000}
                  onClose={() => {
                    setToast(toast.filter((v, i) => i !== id));
                  }}
                >
                  <Alert
                    onClose={() => {
                      setToast(toast.filter((v, i) => i !== id));
                    }}
                    severity={item.severity}
                    sx={{ width: "100%" }}
                    variant="filled"
                  >
                    {item.message}
                  </Alert>
                </Snackbar>
              );
            })}

            <Stack>
              <Divider sx={{ mb: 1, mt: 2 }} />

              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="flex-end"
                spacing={{ xs: 1, md: 2 }}
                sx={{ py: 2 }}
              >
                <div>
                  <Button
                    color="primary"
                    // disabled={formik.isSubmitting}
                    fullWidth
                    size="large"
                    onClick={() => router.goBack()}
                    type="submit"
                    variant="text"
                  >
                    Cancel
                  </Button>
                </div>
                {doesUserHavePermission ? (
                  <div>
                    <Button
                      color="primary"
                      disabled={
                        !(
                          formik.isValid &&
                          !formik.isSubmitting &&
                          formik.dirty
                        )
                      }
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  ""
                )}
              </Stack>
            </Stack>
          </form>
        </Container>
      </Box>
      <Places
        orgId={params.id}
        addresses={addresses}
        doesUserHavePermission={doesUserHavePermission}
      />

      <UsersInvitationDialog
        open={inviteDialog.isOpen}
        handleClose={handleUserInviteDialogClose}
        setToast={setToast}
        toast={toast}
        restrictedDomainValues={domainValues}
        orgName={org.name}
        orgId={params.id}
      />
      <AlertDialog handleClose={handleClose} open={open} edit />
    </>
  );
};
